import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import type { InvoiceSettings } from '@/types/invoice';

/**
 * Fetch invoice settings for the current user
 */
export function useInvoiceSettings() {
  return useQuery({
    queryKey: ['invoiceSettings'],
    queryFn: async () => {
      const { data } = await api.get<InvoiceSettings>('/api/invoices/settings');
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Update invoice settings
 */
export function useUpdateInvoiceSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (settings: Partial<InvoiceSettings>) => {
      const { data } = await api.put<InvoiceSettings>(
        '/api/invoices/settings',
        settings
      );
      return data;
    },
    onSuccess: (updatedSettings) => {
      // Update cache
      queryClient.setQueryData(['invoiceSettings'], updatedSettings);

      // Also invalidate user profile since GST fields are stored there too
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

/**
 * Check if invoice settings are configured
 * Returns true if user has completed the invoice settings wizard
 */
export function useHasInvoiceSettings() {
  const { data: settings, isLoading } = useInvoiceSettings();

  return {
    hasSettings: !!settings?.id,
    isConfigured:
      !!settings &&
      (!!settings.is_gst_registered
        ? !!(settings.gstin && settings.pan && settings.state_code)
        : true),
    isLoading,
    settings,
  };
}
